import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useContext,
  useMemo,
} from 'react';
import { Group } from 'react-konva';
import {
  GetScoreDefn,
  GetPlatePadding,
  GetDisplayMode,
  DisplayModes,
  GetRelativePointerPosition,
  history,
  isNullOrUndefined,
  GetDetailUIText,
  GetUIRect,
  isCtrlKeyPressed,
  getLayerUserAction,
  isUpperLowerAlphaNumeric,
  GetCanvasLabels,
} from 'utils/Global';
import CreateLayerTools from 'business/plateviewer/layer/CreateLayerTools';

import { getDefaultCaptureProfileId } from 'components/commandpanel/Navigator/DRCIPWUtil';
import LayersRenderer from '../render/experiment/LayersRenderer';
import PlateRenderer from '../render/plate/PlateRenderer';
import RMWCanvas from '../RMWCanvas';
import {
  DetailTextProps,
  ExperimentCanvasProps,
  RMRectangle,
  CanvasTilesCoords,
} from 'business/models/CustomModels';
import { LayerDTO, LayerTypeDTO } from 'business/models/Models';
import { useSelectedDrawingTool } from 'redux/hooks/well';
import { OverlayTools } from 'components/well/drawingManager/constants';
import {
  usePreserveExperiment,
  useRedoExperiment,
  useRMDispatch,
  useRMSelector,
  useUndoExperiment,
} from 'redux/hooks';
import { DrawingLayer } from 'business/models/DrawingLayer';
import {
  selectAutoSelectNextWell,
  selectLinkedLayers,
  selectIsEditable,
  selectIsModifyAccessRights,
  selectHitWells,
  selectProductEdition,
} from 'redux/selectors/StateSelectors';
import ResizeLayerTools from 'business/plateviewer/layer/ResizeLayerTools';
import 'App.css';
import { ExpDashboardContext } from 'components/main/ExperimentDashboardContext';
import { FileImportView } from './FileImportView';
import {
  setSelectedLayerId,
  updateExperimentLayers,
  setIsLayerSelected,
  setExpPlateTilesCoords,
  processLayerCreation,
  setWellNavigationDetails,
} from 'redux/reducers/experimentDataReducer';
import {
  selectIsLayerSelected,
  selectSelectedLayer,
} from 'redux/selectors/ExperimentStateSelector';
import {
  getAllWellDetails,
  getPlateLocationByNextWellLocation,
  wellComparisonPredicate,
} from 'utils/ExperimentUtils';
import { useLicenseAuthorizeCommand } from 'CustomHooks/useLicenseAuthorizeCommand';
import { EKeyCodes, KeyboardKeys } from 'constants/types';
import {
  useLayerDeleteHandler,
  usePlateImageStructure,
  useAllWellNavigationDetails,
  useSelectedDropImageParameters,
} from 'redux/hooks/experiment';
import { isEqual } from 'lodash';
import { useCanvasPreference, useIsScreenInUse } from 'redux/hooks/common';
import { ECanvasPreferencesKeys } from 'enums/ECanvasPreferencesKeys';
import PlatesImagesViewer from '../render/plate/plateviewer/imageView/PlatesImagesViewer';
import { TraversalDirection } from 'components/well/slideShow/constants';
import { getWellNavigationDetails } from 'components/well/slideShow/utils';
import {
  WellNavigationDetails,
  isScoringFeatureEnabled,
} from 'components/Experiment/constants';
import {
  isMotherPlateEnabled,
  isScoringWithoutImagesEnabled,
} from 'components/Experiment/utils';
import { useModalHandlers } from 'components/modal';
import { getNormalizedKeyboardKey } from 'keyboardShortcuts/utils';
import {
  addNewLayer,
  checkPlateForPoint,
  getCanvasTilesCoordsForPlate,
  getWellsInRange,
} from './utils';
import {
  useGetWellSelectionContent,
  useUpdateLayerStates,
  useZoomToPlateHandler,
} from './hooks';
import { useCanvasDragDropHandlers } from './canvasDragAndDrop/hooks';
import { ESidePanelTabs } from 'components/main/constants';
import { CanvasDragAndDrop } from './canvasDragAndDrop';
import Konva from 'konva';
import { useStore } from 'react-redux';
import { useScoreExperimentWells } from 'components/Experiment/hooks';

let layerRef: Konva.Layer | null = null;
let errorLayerIconRef: React.MutableRefObject<Konva.Layer> | null = null;
let layerTools: CreateLayerTools;
let designCellRects = [];
let platesBarCode = [];
let indicatorText = [];
let _zoomLevel = 0;
let data: ExperimentCanvasProps = null;
let resizeTool: ResizeLayerTools;
let selectedLayerIndex;
let drawingLinkedLayer = false;
let isDrawingRect = false;
let startWellLoc;
let wellSelection = false;
let startWellWellCoordinates;
let endWellWellCoordinates;

const isImageVisible = () => {
  return (
    GetDisplayMode() === DisplayModes.IMAGE ||
    GetDisplayMode() === DisplayModes.IMAGESCORE
  );
};

function ExperimentCanvas(props: ExperimentCanvasProps) {
  const [drawingLayer, setDrawingLayer] = useState<Konva.Layer | null>(null);
  const selectedDrawingTool = useSelectedDrawingTool();
  const isPanMode = selectedDrawingTool === OverlayTools.PAN;
  const isEditMode: boolean = useRMSelector(selectIsEditable);
  const modifyAccessRights: boolean = useRMSelector(selectIsModifyAccessRights);
  const autoSelectNextWell = useRMSelector(selectAutoSelectNextWell);
  const selectedLayer = useRMSelector(selectSelectedLayer);
  const linkedLayer = useRMSelector(selectLinkedLayers);
  const expDashboardContext = useContext(ExpDashboardContext);

  const expHitWells = useRMSelector(selectHitWells);
  const isLayerSelected = useRMSelector(selectIsLayerSelected);
  const [cellRects, setCellRects] = useState([]);
  const licenseAuthCommand = useLicenseAuthorizeCommand();
  const productEdition = useRMSelector(selectProductEdition);
  const plateImagesStructure = usePlateImageStructure();
  const dispatch = useRMDispatch();
  const allWellNavigationDetails = useAllWellNavigationDetails();
  const selectedDropImageParameters = useSelectedDropImageParameters();
  const { wellNum, plateID, plateNum } =
    allWellNavigationDetails?.[allWellNavigationDetails?.length - 1] || {};
  const { cpId, dropNumber, inspection, regionName } = selectedDropImageParameters || {};
  const imageBatchId = inspection?.groupedInspections?.find(
    (gi) => gi.plateId === plateID,
  )?.imageBatchId;

  const scoreExperimentWells = useScoreExperimentWells();
  const { showModal } = useModalHandlers();

  const setLayerSelectionState = useCallback(
    (isSelected) => dispatch(setIsLayerSelected(isSelected)),
    [dispatch],
  );

  const _setSelectedLayerId = useCallback(
    (id: number) => dispatch(setSelectedLayerId(id)),
    [dispatch],
  );

  const _setLayers = useCallback(
    (layers: DrawingLayer[]) => dispatch(updateExperimentLayers(layers)),
    [dispatch],
  );
  data = props;
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [resizeLayerMode, setResizeLayerMode] = useState(false);
  const [errorLayer, setErrorLayer] =
    useState<React.MutableRefObject<Konva.Layer> | null>(null);
  const isFirstRender = useRef(true);
  const [isControlKeyPressed, setIsControlKeyPressed] = useState(false);
  const [canvasScale, setCanvasScale] = useState(1);
  const Undo = useUndoExperiment();
  const Redo = useRedoExperiment();
  const exp = useRMSelector((state) => state.experiment.expData);
  const plateImageStructure = useRMSelector(
    (state) => state.experiment.plateImageStructure,
  );
  const _setExpPlateTilesCoords = useCallback(
    (canvasTilesCoords: CanvasTilesCoords) =>
      dispatch(setExpPlateTilesCoords(canvasTilesCoords)),
    [dispatch],
  );
  const callPreserveUndoableState = usePreserveExperiment();
  const handleDeleteLayer = useLayerDeleteHandler();
  const selectedWellColor = useCanvasPreference(ECanvasPreferencesKeys.SelectedWellColor);
  const selectedLayerColor = useCanvasPreference(
    ECanvasPreferencesKeys.SelectedLayerColor,
  );
  const isScreenInUse: boolean = useIsScreenInUse();
  const isResizeDisabledForScreenInUse = props.isScreenRoute && isScreenInUse;
  const allowHotKeyDeleteScore = useCanvasPreference(ECanvasPreferencesKeys.DeleteScore);
  const { canvasContainerRef, isCanvasInFocus } = expDashboardContext;
  const [expClickedIngredientWells, setClickedIngredientWells] = useState<any[]>([]);
  const isScoringWithoutImagesAllowed = useCanvasPreference(
    ECanvasPreferencesKeys.ScoreWithoutImages,
  );
  const strokeNormalizer = exp ? Math.max(exp.colCount, exp.rowCount) : 1;
  const wellSelectionContent = useGetWellSelectionContent(
    exp,
    allWellNavigationDetails,
    selectedWellColor,
    strokeNormalizer,
  );
  const updateLayerStates = useUpdateLayerStates();
  const stageRef = useRef<Konva.Stage>();
  const isPlateGenerated = exp.plateDto.some((p) => p.plateId > 0);
  const store = useStore();
  const zoomToPlateHandler = useZoomToPlateHandler(stageRef);
  const isExperimentLoaded = () => !store.getState().experiment.isFetchingExperimentData;
  const [layerErrorIcon, setLayerErrorIcon] = useState<any>(null);
  const { onDrop, onDragOver, dragOverlayRect, onDragLeave, layerOverlayRect } =
    useCanvasDragDropHandlers({
      stageRef,
      exp,
      updateLayerStates,
      experimentId: props.expid,
      isScreenRoute: props.isScreenRoute,
    });
  function handleUserKeyPress(e) {
    setIsControlKeyPressed(isCtrlKeyPressed(e));
  }

  useEffect(() => {
    if (exp?.plateDto.length > 0) {
      drawCellRects();
    }
  }, [expHitWells, props.generateRequired, expClickedIngredientWells]);

  useEffect(() => {
    'keyup keydown'.split(' ').forEach(function (e) {
      window.addEventListener(e, handleUserKeyPress);
    });

    return () => {
      'keyup keydown'.split(' ').forEach(function (e) {
        window.removeEventListener(e, handleUserKeyPress);
      });
    };
  }, []);

  useEffect(() => {
    if (exp?.layers?.length > 0 && exp?.plateDto?.[0]?.ingredients) {
      dispatch(processLayerCreation());
    }
  }, [exp?.layers]);

  useEffect(() => {
    if (
      isNullOrUndefined(drawingLayer) ||
      isNullOrUndefined(exp?.containerRows) ||
      isNullOrUndefined(exp?.containerCols) ||
      isNullOrUndefined(exp?.plateDto) ||
      !exp?.plateDto?.[0] ||
      isNullOrUndefined(exp?.plateDto[0]?.width) ||
      isNullOrUndefined(exp?.plateDto[0]?.height)
    ) {
      return;
    }
    drawCellRects();

    if (props.setGenerateRequired) {
      props.setGenerateRequired(false);
    }
  }, [exp, drawingLayer]);

  useEffect(() => {
    if (!drawingLayer) {
      return;
    }
    if (selectedLayer && selectedLayer.id) {
      resizeTool = new ResizeLayerTools(
        drawingLayer,
        exp,
        getWellFromAbsPoint,
        getPlatePoint,
        selectedLayerColor,
        strokeNormalizer,
      );
      resizeTool.onMouseDown(selectedLayer.id, true);
    } else {
      drawingLayer.destroyChildren();
      drawingLayer.clear();
      drawingLayer.batchDraw();
    }
  }, [selectedLayer, drawingLayer, isLayerSelected]);

  useEffect(() => {
    if (isNullOrUndefined(selectedLayer)) {
      return;
    }
    if (isNullOrUndefined(exp?.layers)) {
      return;
    }
    let lyr = exp?.layers.find((l) => l.id === selectedLayer.id);
    if (isNullOrUndefined(lyr) && selectedLayer.type === LayerTypeDTO.Protein) {
      lyr = exp?.layers.find((l) => l.id === selectedLayer.id);
    }
    if (
      GetDisplayMode() !== DisplayModes.DESIGN ||
      (!isNullOrUndefined(exp?.layers) && exp?.layers.length === 0) ||
      isNullOrUndefined(lyr)
    ) {
      if (isNullOrUndefined(drawingLayer)) {
        return;
      }
      drawingLayer.destroyChildren();
      drawingLayer.clear();
      drawingLayer.batchDraw();
      //_setSelectedLayer(null);
    }

    if (
      !isNullOrUndefined(lyr) &&
      (lyr.x !== selectedLayer.x || lyr.y !== selectedLayer.y)
    ) {
      _setSelectedLayerId(lyr.id);
    }
  });

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
    } else {
      if (!isNullOrUndefined(linkedLayer)) {
        resizeTool = new ResizeLayerTools(
          drawingLayer,
          { ...exp },
          getWellFromAbsPoint,
          getPlatePoint,
          selectedLayerColor,
          strokeNormalizer,
        );
        const layer = { ...linkedLayer };
        resizeTool.drawLinkedLayer(layer);
        drawingLinkedLayer = true;
        //setResizeLayerMode(true);
      }
    }
  }, [linkedLayer]);

  function drawCellRects() {
    const cells = [];
    const indicators = [];

    const rows = exp?.containerRows;
    const cols = exp?.containerCols;
    if (!exp?.plateDto?.[0]) {
      return;
    }
    const plate = exp?.plateDto[0];
    const { width, height } = plate;
    if (
      isNullOrUndefined(rows) ||
      isNullOrUndefined(cols) ||
      isNullOrUndefined(width) ||
      isNullOrUndefined(height)
    ) {
      return;
    }
    designCellRects = [];
    platesBarCode = [];
    exp?.plateDto.forEach((plate) => {
      if (isNullOrUndefined(plate.wellRects) || plate.wellRects.length === 0) {
        return;
      }

      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const wellRect: RMRectangle = plate.wellRects[r * cols + c];
          const rect: RMRectangle = {
            x: wellRect.x,
            y: wellRect.y,
            w: wellRect.w,
            h: wellRect.h,
            bColor: 'black',
          };
          const designRect: RMRectangle = {
            x: wellRect.x,
            y: wellRect.y,
            w: wellRect.w,
            h: wellRect.h,
            bColor: 'transparent',
          };
          rect.listening = false;
          designRect.listening = false;

          rect.opacity = 1;
          rect.fillColor = '';
          designRect.opacity = 1;
          designRect.fillColor = '';
          if (!isNullOrUndefined(expHitWells)) {
            if (
              !expHitWells.some(
                (well) =>
                  well.plateId === plate.plateId && well.wellNumber === r * cols + c + 1,
              )
            ) {
              rect.opacity = 0.8;
              rect.fillColor = '#DBF0F9';
              designRect.opacity = 0.8;
              designRect.fillColor = '#DBF0F9';
            }
          }

          if (expClickedIngredientWells && expClickedIngredientWells.length > 0) {
            if (
              !expClickedIngredientWells.some(
                (well) =>
                  well.plateId === plate.plateId && well.wellNumber === r * cols + c + 1,
              )
            ) {
              rect.opacity = 0.8;
              rect.fillColor = '#DBF0F9';
              designRect.opacity = 0.8;
              designRect.fillColor = '#DBF0F9';
            }
          }
          cells.push(GetUIRect(rect));
          designCellRects.push(GetUIRect(designRect));
        }
      }
      const canvasLabels = GetCanvasLabels();
      for (let r = 0; r < canvasLabels?.rowLabels?.length; r++) {
        const wellRect: DetailTextProps = plate.wellRects[r * cols];
        const indicatorText: DetailTextProps = {
          x: wellRect.x - wellRect.w / 2,
          y: wellRect.y,
          w: wellRect.w / 2,
          h: wellRect.h,
          size: wellRect.h / 5,
          name: canvasLabels?.rowLabels?.[r],
          stroke: 'black',
          strokeWidth: 0.2 / strokeNormalizer,
          align: null,
          fill: 'black',
        };
        indicators.push(GetDetailUIText(indicatorText));
      }
      for (let c = 0; c < canvasLabels?.columnLabels?.length; c++) {
        const wellRect = plate.wellRects[c];
        const indicatorText: DetailTextProps = {
          x: wellRect.x,
          y: wellRect.y - wellRect.h / 2,
          w: wellRect.w,
          h: wellRect.h / 2,
          size: wellRect.h / 5,
          name: `${canvasLabels?.columnLabels?.[c]}`,
          fill: 'black',
          stroke: null,
          strokeWidth: null,
          align: null,
        };
        indicators.push(GetDetailUIText(indicatorText));
      }

      const wellRect = plate.wellRects[rows * cols - 1];
      const text: DetailTextProps = {
        x: wellRect.x - wellRect.w - 1, // just to avoid layer border overlap
        y: wellRect.y + wellRect.h,
        w: wellRect.w * 2,
        h: wellRect.h / 2,
        size: wellRect.h / 6,
        name: plate.plateBarName,
        align: 'right',
        fill: 'blue',
        stroke: null,
        strokeWidth: null,
      };
      const plateBarName = GetDetailUIText(text);
      platesBarCode.push(plateBarName);
    });
    indicatorText = indicators;
    setCellRects(cells);
  }
  function getClickHandler(e) {
    if (
      e.evt.detail === 2 && // evt = 1 (single click ) is removed, as selection will be handled by mouseDown event. only evt = 2 (double click) will work from here
      (GetDisplayMode() === DisplayModes.IMAGE ||
        GetDisplayMode() === DisplayModes.IMAGESCORE ||
        GetDisplayMode() === DisplayModes.SCORE)
    ) {
      if (props.isScreenRoute !== undefined) {
        return;
      }
      onDblClick();
    }
  }

  async function onZoomChange(stage, scale) {
    if (stage) {
      const currentScale = stage.scaleX?.() ?? scale;
      const hasRealScaleChange =
        Math.abs((currentScale ?? 0) - (canvasScale ?? 0)) > 0.001;
      // Ignore layout-triggered callbacks (for example side panel open/close best-fit)
      // when zoom scale did not actually change.
      if (!hasRealScaleChange) {
        return;
      }
    }
    const level = 1;
    const is1536WellPlate = exp?.containerRows * exp?.containerCols === 1536;
    exp?.plateDto.forEach((plate) => {
      const plateData = getCanvasTilesCoordsForPlate(
        plate,
        stage,
        scale,
        level,
        is1536WellPlate,
      );
      if (!plateData) {
        return;
      }
      _setExpPlateTilesCoords(plateData);
    });
    _zoomLevel = level;
    setCanvasScale(scale);
  }

  function onMouseDown(evt) {
    if (!isExperimentLoaded()) {
      return;
    }
    if (!isNullOrUndefined(props.loadingDetails) && props.loadingDetails.length > 0) {
      return;
    }
    if (isPanMode && evt.evt.button === 0) {
      return;
    }
    setIsMouseDown(true);
    if (evt.evt.button == 1) {
      stageRef?.current.draggable(true);
    } else if (evt.evt.button == 0 || evt.evt.button == 2) {
      if (
        exp?.layers &&
        !isNullOrUndefined(
          exp?.layers.find(
            (lyr) => lyr.type == LayerTypeDTO.Grid && isNullOrUndefined(lyr.layerDetail),
          ),
        )
      ) {
        return;
      }
      if (drawingLinkedLayer) {
        const mouseUpResult = resizeTool.onMouseUp(true);
        const tempLayers = mouseUpResult.layers;
        setResizeLayerMode(false);

        drawingLayer.destroyChildren();
        drawingLayer.clear();
        drawingLayer.batchDraw();

        _setSelectedLayerId(tempLayers[0].id);
        props.changeShowLayers(true);
        _setLayers(tempLayers);
        setLayerSelectionState(true);
        drawingLinkedLayer = false;
        return;
      }
      if (resizeLayerMode) {
        onMouseUp(evt);
        return;
      }
      resizeTool = new ResizeLayerTools(
        drawingLayer,
        exp,
        getWellFromAbsPoint,
        getPlatePoint,
        selectedLayerColor,
        strokeNormalizer,
      );
      const clickResult = resizeTool.onMouseDown(null, false, selectedLayer?.id);
      selectedLayerIndex = clickResult.selectedLayer;
      if (!isNullOrUndefined(clickResult.layers)) {
        if (canvasContainerRef?.current?.className == 'cursor-pointer') {
          canvasContainerRef.current.className = 'grabbing-pointer';
        }
        if (!isResizeDisabledForScreenInUse) {
          licenseAuthCommand(
            () => setResizeLayerMode(true),
            getLayerUserAction(exp?.layers?.[selectedLayerIndex]?.type),
          )();
        }
      }
      if (selectedLayerIndex >= 0) {
        setLayerSelectionState(true);
        _setSelectedLayerId(exp?.layers[selectedLayerIndex].id);
        props.setSelectedSidePanelTab(ESidePanelTabs.Layers);
      } else {
        setLayerSelectionState(false);
      }

      if (props.isLayerDrawingMode && GetDisplayMode() === DisplayModes.DESIGN) {
        isDrawingRect = true;
        layerTools = new CreateLayerTools(drawingLayer, strokeNormalizer);
        layerTools.onMouseDown(evt);
        startWellLoc = GetRelativePointerPosition(drawingLayer);
        if (
          GetDisplayMode() === DisplayModes.DESIGN ||
          (GetDisplayMode() === DisplayModes.WELLINGREDIENT &&
            evt.target.attrs.wellPositionDetails)
        ) {
          startWellWellCoordinates = evt.target.attrs.wellPositionDetails;
          endWellWellCoordinates = startWellWellCoordinates;
        }
      }

      if (
        !(props.isLayerDrawingMode && GetDisplayMode() === DisplayModes.DESIGN) &&
        !resizeLayerMode &&
        !drawingLinkedLayer &&
        !clickResult.layers
      ) {
        wellSelection = true;
        isDrawingRect = true;
        layerTools = new CreateLayerTools(drawingLayer, strokeNormalizer);
        layerTools.onMouseDown(evt);
        startWellLoc = GetRelativePointerPosition(drawingLayer);
        if (
          GetDisplayMode() === DisplayModes.DESIGN ||
          (GetDisplayMode() === DisplayModes.WELLINGREDIENT &&
            evt.target.attrs.wellPositionDetails)
        ) {
          startWellWellCoordinates = evt.target.attrs.wellPositionDetails;
          endWellWellCoordinates = startWellWellCoordinates;
        }
      }
    }
  }

  function onMouseUp(evt) {
    if (!isExperimentLoaded()) {
      return;
    }
    if (!isNullOrUndefined(props.loadingDetails) && props.loadingDetails.length > 0) {
      return;
    }

    setIsMouseDown(false);
    if (evt.evt.button == 1 && !isPanMode) {
      stageRef?.current.draggable(false);
    }
    if (resizeLayerMode) {
      wellSelection = false;
      const resizeResult: {
        layers: LayerDTO[];
        generateRqd: boolean;
        selectedId: number;
      } = resizeTool.onMouseUp();

      _setSelectedLayerId(resizeResult.selectedId);
      if (resizeResult.generateRqd) {
        callPreserveUndoableState();
        _setLayers(resizeResult.layers);
      }
      if (canvasContainerRef.current) {
        canvasContainerRef.current.className = '';
      }
      props.changeShowLayers(true);
      setResizeLayerMode(false);
      return;
    }
    if (isDrawingRect === true) {
      const point = GetRelativePointerPosition(drawingLayer);
      const startWell = getWellFromAbsPoint(
        startWellLoc,
        true,
        isEqual(startWellLoc, point),
      );
      let endWell = getWellFromAbsPoint(point, true, isEqual(startWellLoc, point));
      if (startWell && !endWell) {
        const platePoint = getPlatePoint(point, true);
        endWell = {
          x: platePoint.plate.colPos * platePoint.plate.cols - 1,
          y: platePoint.plate.rowPos * platePoint.plate.rows - 1,
        };
      }
      if (startWell && endWell) {
        if (!wellSelection && startWellWellCoordinates && endWellWellCoordinates) {
          if (isEqual(startWellWellCoordinates, endWellWellCoordinates)) {
            startWellWellCoordinates = {
              x: (startWellWellCoordinates.colPos - 1) * exp?.containerCols,
              y: (startWellWellCoordinates.rowPos - 1) * exp?.containerRows,
            };
            endWellWellCoordinates = {
              x: startWellWellCoordinates.x + exp?.containerCols - 1,
              y: startWellWellCoordinates.y + exp?.containerRows - 1,
            };
          }

          if (exp?.layers === undefined) {
            exp.layers = [];
          }
          const x =
            startWellWellCoordinates.x < endWellWellCoordinates.x
              ? startWellWellCoordinates.x
              : endWellWellCoordinates.x; // get smaller point as starting point
          const y =
            startWellWellCoordinates.y < endWellWellCoordinates.y
              ? startWellWellCoordinates.y
              : endWellWellCoordinates.y;
          const width = Math.abs(endWellWellCoordinates.x - startWellWellCoordinates.x);
          const height = Math.abs(endWellWellCoordinates.y - startWellWellCoordinates.y);
          const newLayer = addNewLayer(
            x,
            y,
            width,
            height,
            props.drawingLayerType,
            exp,
            props.expid,
          );
          updateLayerStates(newLayer);
        } else if (wellSelection) {
          let selectedWells: WellNavigationDetails[] = [];
          if (
            GetDisplayMode() === DisplayModes.IMAGE ||
            GetDisplayMode() === DisplayModes.SCORE
          ) {
            let isReversedVertically: boolean = false;
            let isReversedHorizontally: boolean = false;

            if (startWell.x > endWell.x) {
              const tempX = startWell.x;
              startWell.x = endWell.x;
              endWell.x = tempX;
              isReversedHorizontally = true;
            }

            if (startWell.y > endWell.y) {
              const tempY = startWell.y;
              startWell.y = endWell.y;
              endWell.y = tempY;
              isReversedVertically = true;
            }

            let plateOfWellLocation = {
              rowPos: 1,
              colPos: 1,
            };
            const xDiff = endWell.x - startWell.x;
            const yDiff = endWell.y - startWell.y;

            let colNumber = isReversedVertically ? yDiff : 0;
            let rowNumber = isReversedHorizontally ? xDiff : 0;

            for (let column = startWell.y; column <= startWell.y + yDiff; column++) {
              let startingColumnOfSelectedWells = 0;
              if (column + 1 > exp?.containerRows) {
                startingColumnOfSelectedWells = column % exp?.containerRows;
              } else {
                startingColumnOfSelectedWells = column;
              }
              plateOfWellLocation = getPlateLocationByNextWellLocation(
                plateOfWellLocation,
                startWell,
                exp?.containerRows,
                exp?.containerCols,
                column,
                null,
              );
              const absoluteXAxisPosition =
                startWell.x > exp?.containerCols
                  ? startWell.x % exp?.containerCols
                  : startWell.x;
              const startingWellOfColumn =
                startingColumnOfSelectedWells * exp?.containerCols +
                absoluteXAxisPosition +
                1;
              for (let row = 0; row <= xDiff; row++) {
                let wellNum = startingWellOfColumn + row;
                if (startWell.x + row + 1 > exp?.containerCols) {
                  let rowsToAdd = 0;
                  if ((startWell.x + row + 1) % exp?.containerCols === 0) {
                    rowsToAdd = exp?.containerCols;
                  } else {
                    rowsToAdd = (startWell.x + row + 1) % exp?.containerCols;
                  }
                  wellNum =
                    rowsToAdd + startingColumnOfSelectedWells * exp?.containerCols;
                }
                plateOfWellLocation = getPlateLocationByNextWellLocation(
                  plateOfWellLocation,
                  startWell,
                  exp?.containerRows,
                  exp?.containerCols,
                  null,
                  row,
                );
                const plateOfWell = exp?.plateDto.find(
                  (plate) =>
                    plate.colPos == plateOfWellLocation.colPos &&
                    plate.rowPos == plateOfWellLocation.rowPos,
                );
                const wellDetails: WellNavigationDetails = {
                  wellNum,
                  plateNum: plateOfWell.plateNumber,
                };
                if (plateOfWell?.plateId) {
                  wellDetails.plateID = plateOfWell?.plateId;
                }
                wellDetails.selectionOrder = (xDiff + 1) * colNumber + rowNumber;
                selectedWells.push(wellDetails);
                rowNumber = isReversedHorizontally ? rowNumber - 1 : rowNumber + 1;
              }
              colNumber = isReversedVertically ? colNumber - 1 : colNumber + 1;
              rowNumber = isReversedHorizontally ? xDiff : 0;
            }
          } else {
            if (startWellWellCoordinates && endWellWellCoordinates) {
              selectedWells = getWellsInRange(
                startWellWellCoordinates,
                endWellWellCoordinates,
                exp,
              );
            }
          }

          if (selectedWells.length > 0) {
            if (evt.evt.button === 2) {
              const rightClickedWell = selectedWells[0];
              const isRightClickedWellInSelection = allWellNavigationDetails.some((w) =>
                wellComparisonPredicate(w, rightClickedWell),
              );
              if (!isRightClickedWellInSelection) {
                dispatch(setWellNavigationDetails(selectedWells));
              }
            } else if (evt.evt.button === 0) {
              if (isControlKeyPressed) {
                const newWellDetails: WellNavigationDetails[] = [];

                allWellNavigationDetails.forEach((item) => {
                  const newItem = {
                    ...item,
                    selectionOrder: !isNullOrUndefined(item.selectionOrder)
                      ? item.selectionOrder + selectedWells.length
                      : null,
                  };

                  if (
                    !selectedWells.some(
                      (item2) =>
                        wellComparisonPredicate(newItem, item2) &&
                        allWellNavigationDetails.length > 1 &&
                        selectedWells.length === 1,
                    )
                  ) {
                    newWellDetails.push(newItem);
                  }
                });

                selectedWells.forEach((item2) => {
                  if (
                    !allWellNavigationDetails.some((item1) =>
                      wellComparisonPredicate(item2, item1),
                    )
                  ) {
                    newWellDetails.push(item2);
                  }
                });

                dispatch(setWellNavigationDetails(newWellDetails));
              } else {
                dispatch(setWellNavigationDetails(selectedWells));
              }
            }
          }
        }
      }

      // drawing layer shld be cleaned after current operation.. its just to show currently interacted drawing

      drawingLayer?.destroyChildren();
      drawingLayer?.clear();
      drawingLayer?.batchDraw();
      props.changeShowLayers(true);
    }

    isDrawingRect = false;
    wellSelection = false;

    if (
      evt.evt.button === 0 &&
      evt.evt.altKey &&
      GetDisplayMode() === DisplayModes.DESIGN
    ) {
      const _stg = evt.currentTarget;
      const points = GetRelativePointerPosition(_stg);
      if (!evt.target.attrs?.ingredientDetails) {
        return;
      }

      const ingredient = evt.target.attrs.ingredientDetails.find(
        (_ing) =>
          _ing?.type == 'ingredient' && points.y >= _ing.y && points.y <= _ing.y + _ing.h,
      );

      const clickedIngeredientWells = [];

      exp.plateDto.forEach((plate) => {
        plate.ingredients.wellList.forEach((well) => {
          well.wellLayerList.forEach((wellLayer) => {
            wellLayer.wellLayerIngredientList.forEach((wellLayerIngredient) => {
              if (
                wellLayerIngredient.ingredientStockId == ingredient?.ingredientStockId
              ) {
                const clickedIngredientWell = {
                  wellNumber: well.wellNumber,
                  plateId: plate.plateId,
                };
                clickedIngeredientWells.push(clickedIngredientWell);
              }
            });
          });
        });
      });

      setClickedIngredientWells(clickedIngeredientWells);
    } else {
      setClickedIngredientWells([]);
    }
  }

  let timeout;
  function onMouseMove(evt) {
    if (
      (resizeLayerMode || (props.isLayerDrawingMode && isDrawingRect)) &&
      evt.evt.buttons == 0
    ) {
      onMouseUp(evt);
      return;
    }

    if (evt.evt.buttons != 1) {
      wellSelection = false;
      isDrawingRect = false;
    }
    if (
      (resizeLayerMode || drawingLinkedLayer) &&
      isEditMode &&
      modifyAccessRights &&
      !isResizeDisabledForScreenInUse
    ) {
      resizeTool.onMouseMove(evt);
    } else if (
      isDrawingRect === true &&
      (wellSelection ||
        (props.isLayerDrawingMode && GetDisplayMode() === DisplayModes.DESIGN))
    ) {
      let stWellRect;
      let endWellRect;

      if (
        (GetDisplayMode() === DisplayModes.DESIGN ||
          GetDisplayMode() === DisplayModes.WELLINGREDIENT) &&
        evt.target.attrs.wellPositionDetails &&
        startWellWellCoordinates
      ) {
        endWellWellCoordinates = evt.target.attrs.wellPositionDetails;
        const startPlate = exp?.plateDto.find(
          (plate) =>
            plate.colPos == startWellWellCoordinates.colPos &&
            plate.rowPos == startWellWellCoordinates.rowPos,
        );
        let plateWellX = startWellWellCoordinates.x % exp?.containerCols;
        let plateWellY = startWellWellCoordinates.y % exp?.containerRows;
        stWellRect = startPlate.wellRects[plateWellX + plateWellY * exp?.containerCols];
        const endPlate = exp?.plateDto.find(
          (plate) =>
            plate.colPos == endWellWellCoordinates.colPos &&
            plate.rowPos == endWellWellCoordinates.rowPos,
        );
        plateWellX = endWellWellCoordinates.x % exp?.containerCols;
        plateWellY = endWellWellCoordinates.y % exp?.containerRows;
        endWellRect = endPlate.wellRects[plateWellX + plateWellY * exp?.containerCols];
      } else if (
        GetDisplayMode() === DisplayModes.IMAGE ||
        GetDisplayMode() === DisplayModes.SCORE
      ) {
        const point = GetRelativePointerPosition(drawingLayer);
        const startWell = getWellFromAbsPoint(startWellLoc, true);
        const endWell = getWellFromAbsPoint(point, true);
        let platePoint = getPlatePoint(startWellLoc);
        let plateWellX = startWell.x % exp?.containerCols;
        let plateWellY = startWell.y % exp?.containerRows;
        stWellRect = platePoint.plate.wellRects
          ? platePoint.plate.wellRects[plateWellX + plateWellY * exp?.containerCols]
          : null;
        platePoint = getPlatePoint(point);
        plateWellX = endWell.x % exp?.containerCols;
        plateWellY = endWell.y % exp?.containerRows;
        endWellRect = platePoint.plate.wellRects
          ? platePoint.plate.wellRects[plateWellX + plateWellY * exp?.containerCols]
          : null;
      }

      if (
        stWellRect &&
        endWellRect &&
        (!wellSelection ||
          !(stWellRect.x === endWellRect.x && stWellRect.y === endWellRect.y))
      ) {
        layerTools.onMouseMove(evt, stWellRect, endWellRect);
      }
    } else {
      // Adding delay in OnMouseEvent to avoid exessive calculation cost
      if (timeout !== undefined) {
        window.clearTimeout(timeout);
      }
      timeout = window.setTimeout(mouseEndEvent, 10);
    }

    function mouseEndEvent() {
      const resizingTool = new ResizeLayerTools(
        drawingLayer,
        exp,
        getWellFromAbsPoint,
        getPlatePoint,
        selectedLayerColor,
        strokeNormalizer,
      );
      if (
        exp?.layers != undefined &&
        exp?.layers.length > 0 &&
        canvasContainerRef.current
      ) {
        if (GetDisplayMode() === DisplayModes.DESIGN) {
          if (isResizeDisabledForScreenInUse) {
            canvasContainerRef.current.className = 'cursor-default';
          } else {
            canvasContainerRef.current.className = resizingTool.getCursorClass(
              selectedLayer?.id,
            );
          }
        } else if (canvasContainerRef?.current?.className !== 'cursor-default') {
          canvasContainerRef.current.className = 'cursor-default';
        }
      }
    }
  }

  function getPlatePoint(point, movingBackward = false) {
    const plateNumber = checkPlateForPoint(point.x, point.y, exp);
    const plate = exp?.plateDto[plateNumber - 1];
    return {
      x: plate.colPos,
      y: plate.rowPos,
      plate: plate,
    };
  }

  function getWellFromAbsPoint(
    point,
    isStartingWell = false,
    restrictOutsideSelection = false,
  ) {
    const platePoint = getPlatePoint(point, isStartingWell);
    let isLastX = false;
    let isLastY = false;
    //const plate = exp?.plateDto[(platePoint.x * exp?.colCount) + platePoint.y];
    const padding = GetPlatePadding() / 2;

    const paddingX = padding * platePoint.plate.colPos;
    const paddingY = padding * platePoint.plate.rowPos;

    let wellNumXFromStart =
      (point.x - paddingX) / (platePoint.plate.width / exp?.containerCols);
    wellNumXFromStart = wellNumXFromStart >= 0 ? wellNumXFromStart : 0;

    if (wellNumXFromStart > platePoint.plate.colPos * platePoint.plate.cols) {
      if (restrictOutsideSelection) {
        return;
      }
      // if WellX is greater than cols in current plate
      wellNumXFromStart = platePoint.plate.colPos * platePoint.plate.cols - 1; // set wellX to last col of current plate
      isLastX = true; // to prevent 30% check
    }

    let wellNumYFromStart =
      (point.y - paddingY) / (platePoint.plate.height / exp?.containerRows);
    wellNumYFromStart = wellNumYFromStart >= 0 ? wellNumYFromStart : 0;

    if (wellNumYFromStart > platePoint.plate.rowPos * platePoint.plate.rows) {
      if (restrictOutsideSelection) {
        return;
      }
      // if wellY is greater than rows in current plate
      wellNumYFromStart = platePoint.plate.rowPos * platePoint.plate.rows - 1; // set wellY to last row of current plate
      isLastY = true; // to prevetn 30% check
    }

    const wellPoint = {
      x: Math.floor(wellNumXFromStart),
      y: Math.floor(wellNumYFromStart),
    };

    const plateWellX = wellPoint.x % exp?.containerCols;
    const plateWellY = wellPoint.y % exp?.containerRows;
    const wellRect = platePoint.plate.wellRects
      ? platePoint.plate.wellRects[plateWellX + plateWellY * exp?.containerCols]
      : null;

    if (!isStartingWell && !isNullOrUndefined(wellRect)) {
      // If pointer is in starting 30% area of well, move well to previous one
      if (
        (point.x - wellRect.x < wellRect.w * 0.3 || point.x > wellRect.x + wellRect.w) &&
        wellPoint.x > 0 &&
        !isLastX
      ) {
        wellPoint.x--;
      }

      if (
        (point.y - wellRect.y < wellRect.h * 0.3 || point.y > wellRect.y + wellRect.h) &&
        wellPoint.y > 0 &&
        !isLastY
      ) {
        wellPoint.y--;
      }
    }
    return wellPoint;
  }

  function onDblClick() {
    if (
      wellNum <= 0 ||
      wellNum > exp?.plateDto[plateNum - 1]?.cols * exp?.plateDto[plateNum - 1]?.rows ||
      !imageBatchId
    ) {
      return;
    }
    history.push(
      `/exp/${data.expid}/${plateID}/${wellNum}/${dropNumber}/${cpId}/${regionName}/${imageBatchId}/drop`,
    );
  }

  const getAutoSelectNextWellNumToScore = () => {
    if (autoSelectNextWell && !isScoringWithoutImagesAllowed) {
      const wellDetails: WellNavigationDetails = {
        wellNum,
        plateNum,
        plateID,
      };
      const nextWellDetails = getWellNavigationDetails(
        exp?.plateDto,
        wellDetails,
        1,
        TraversalDirection.Row,
        expDashboardContext?.imagedWells,
      );
      return nextWellDetails;
    }
    return null;
  };

  async function onKeyPress(event: KeyboardEvent) {
    if (!isCanvasInFocus) {
      return;
    }
    if (!isExperimentLoaded()) {
      return;
    }
    event.preventDefault();
    // if (props.loadingDetails.length > 0) return;
    // TODOS
    // hotkeys can be max of length two.
    //    like....ab, cs, etc...
    // But in our case...it is working for a single character as it calls a function when we request for a score by pressing any key
    const { key, keyCode } = event;
    const ctrlKey = event.ctrlKey || event.metaKey;

    if (GetDisplayMode() !== DisplayModes.SCORE) {
      if (selectedLayerIndex >= 0 && keyCode == EKeyCodes.Delete) {
        const selectedLayer = exp?.layers[selectedLayerIndex];
        if (selectedLayer) {
          handleDeleteLayer(selectedLayer.id);
        }
      }
    }

    if (ctrlKey && (key === 'z' || key === 'Z')) {
      Undo();
      return;
    } else if (ctrlKey && (key === 'y' || key === 'Y')) {
      Redo();
      return;
    } else if (ctrlKey && event.key === 'a') {
      dispatch(
        setWellNavigationDetails(
          getAllWellDetails(
            allWellNavigationDetails?.[allWellNavigationDetails?.length - 1],
            exp.plateDto,
            exp.containerCols * exp.containerRows,
          ),
        ),
      );
      return;
    }

    /**
     * Handles scoring of wells via alphanumeric hotkeys.
     *
     * Conditions checked:
     * - Ctrl, Alt, or Shift keys must NOT be pressed
     * - Key must be alphanumeric (uppercase or lowercase)
     * - Mother plate must be enabled
     * - Plate must have an image or Scoring without images property must be enabled
     * - Score definitions must be available
     * - The pressed key must match a valid score's hotkey
     *
     * If all conditions pass, dispatches the scoring action for the current well.
     *  * Automatically selects the next well if only one well is selected
     * and auto-selection is enabled.
     */

    if (!ctrlKey && !event.altKey && isUpperLowerAlphaNumeric(key) && isScoringFeatureEnabled(productEdition)) {
      if (
        !(await isMotherPlateEnabled(
          store.getState().experiment?.motherPlatesScoringStatuses,
          allWellNavigationDetails,
          showModal,
        ))
      ) {
        return;
      }
      if (
        !(await isScoringWithoutImagesEnabled(
          allWellNavigationDetails,
          plateImageStructure,
        ))
      ) {
        return;
      }
      if (isNullOrUndefined(GetScoreDefn())) {
        return;
      }
      const score = GetScoreDefn().find((s) => s.hotKey === key);
      if (isNullOrUndefined(score)) {
        return;
      }

      scoreExperimentWells({
        wellDropScoreId: score.wellDropScoreId,
        wellDropScoreGroupId: score.wellDropScoreGroupId,
        disAllowScoreRemove: !allowHotKeyDeleteScore,
        autoSelectNextWell: autoSelectNextWell && allWellNavigationDetails?.length === 1,
        nextWellDetails: getAutoSelectNextWellNumToScore(),
      });
    }
    if (
      !ctrlKey &&
      !event.altKey &&
      !event.shiftKey &&
      (keyCode === 37 || keyCode === 38 || keyCode === 39 || keyCode === 40)
    ) {
      const newWellDetails = getWellNavigationDetails(
        exp?.plateDto,
        allWellNavigationDetails?.[allWellNavigationDetails.length - 1],
        keyCode === 39 || keyCode === 40 ? 1 : -1,
        keyCode === 38 || keyCode === 40
          ? TraversalDirection.Column
          : TraversalDirection.Row,
        [],
      );
      dispatch(setWellNavigationDetails([newWellDetails]));
    } else if (
      ctrlKey &&
      getNormalizedKeyboardKey(event) === KeyboardKeys.Q &&
      exp?.plateDto?.length > 1
    ) {
      zoomToPlateHandler(plateID);
    }

    if (keyCode === 13) {
      onDblClick();
    }
  }

  function setStageRef(
    sRef: Konva.Stage,
    lRef: Konva.Layer,
    lRef2: Konva.Layer,
    eLRef,
    eLIRef,
  ) {
    stageRef.current = sRef;
    layerRef = lRef;
    setDrawingLayer(lRef2); // new Konva.Layer()
    errorLayerIconRef = eLIRef;
    setErrorLayer(eLRef);
    if (props.setCanvasRefs) {
      props.setCanvasRefs(sRef, lRef);
    }
  }

  const getCanvasRefs = useCallback(() => {
    return { stageRef: stageRef?.current, layerRef };
  }, [stageRef?.current, layerRef]);

  const plateRendererComponent = useMemo(() => {
    if (!exp?.plateDto) {
      return [];
    }

    const plates = exp?.plateDto.map((plate, index) => (
      <PlateRenderer
        key={`plateRenderer_${index}`}
        isScreen={data.isScreenRoute}
        mode={props.selectedTabName}
        plateNumber={plate.plateNumber}
        canvasRefs={getCanvasRefs}
        grpRef={layerRef}
        zoomLevel={_zoomLevel}
        isWellSelectionMode={isMouseDown}
        plate={plate}
        strokeNormalizer={strokeNormalizer}
      />
    ));
    return plates;
  }, [
    exp?.plateDto,
    layerRef,
    props.selectedTabName,
    getCanvasRefs,
    GetDisplayMode(),
    _zoomLevel,
    isMouseDown,
    canvasScale,
  ]);

  const plateImageRenderer = () => {
    if (!plateImagesStructure?.length) {
      return [];
    }

    if (!exp?.plateDto?.[0]) {
      return [];
    }

    const captureProfileId = cpId == null ? getDefaultCaptureProfileId(exp) : cpId;
    const plateRows = exp?.plateDto[0]?.rows;
    const plateCols = exp?.plateDto[0]?.cols;
    return (
      <PlatesImagesViewer
        plateImagesStructure={plateImagesStructure}
        canvasRefs={getCanvasRefs}
        zoomLevel={_zoomLevel}
        experimentTuningValues={exp?.experimentTuningValues}
        captureProfileId={captureProfileId}
        plateRows={plateRows}
        plateCols={plateCols}
      />
    );
  };

  if (!exp?.plateDto?.[0]) {
    return null;
  }

  return (
    <>
      <div id="expcanvas-container" ref={expDashboardContext.canvasContainerRef}>
        {layerErrorIcon && (
          <img
            className="z-[99999] m-1 absolute h-4"
            src={layerErrorIcon.src}
            title={layerErrorIcon.msg}
          />
        )}
        <RMWCanvas
          onKeyPress={onKeyPress}
          cursorClass={expDashboardContext.cursorClass}
          setZoomInvocker={props.setZoomInvocker}
          onZoomChange={onZoomChange}
          stageRef={setStageRef}
          onMouseDown={onMouseDown}
          onMouseUp={onMouseUp}
          onMouseMove={onMouseMove}
          draggable={isPanMode ? true : undefined}
          canvasWidth={props.canvasWidth}
          canvasHeight={props.canvasHeight}
          height={
            exp?.plateDto[0]?.height * exp?.rowCount + GetPlatePadding() * exp?.rowCount
          }
          width={
            exp?.plateDto[0]?.width * exp?.colCount + GetPlatePadding() * exp?.colCount
          }
          cols={exp?.containerCols}
          rows={exp?.containerRows}
          maxCanvasWidthPercentage={props.widthMainDiv}
          adjustXonCanvasSizeUpdate={150}
          wellSelectionContent={wellSelectionContent}
          onDblClick={getClickHandler}
          onMouseEnter={() => {
            if (props.isLayerDrawingMode && GetDisplayMode() === DisplayModes.DESIGN) {
              expDashboardContext.setCursorClass('cursor-crosshair');
            }
          }}
          onMouseLeave={() => {
            expDashboardContext.setCursorClass('cursor-default');
          }}
          onDragOver={onDragOver}
          onDrop={onDrop}
          onDragLeave={onDragLeave}
        >
          {isPlateGenerated && (
            <Group id="konva-group-plate-image" visible={isImageVisible()}>
              {plateImageRenderer()}
            </Group>
          )}
          {plateRendererComponent}
          <Group id="konva-group-plate-grid" listening={false}>
            {cellRects === undefined ||
            cellRects.length === 0 ||
            ((GetDisplayMode() == DisplayModes.DESIGN ||
              GetDisplayMode() === DisplayModes.WELLINGREDIENT ||
              data.isScreenRoute) &&
              !isNullOrUndefined(exp?.plateDto[0]?.ingredients))
              ? null
              : cellRects}
          </Group>
          <Group id="konva-group-design-cell-rects" listening={false}>
            {designCellRects.length > 0 &&
            GetDisplayMode() === DisplayModes.DESIGN &&
            !isNullOrUndefined(exp?.plateDto[0]?.ingredients)
              ? designCellRects
              : null}
          </Group>
          <Group id="konva-group-plate-barcode" listening={false}>
            {platesBarCode.length === 0 ? null : platesBarCode}
          </Group>
          <Group id={'konva-group-indicator-text'} listening={false}>
            {indicatorText === undefined || indicatorText.length === 0
              ? null
              : indicatorText}
          </Group>
          <Group
            id="konva-group-design-layers"
            visible={GetDisplayMode() === DisplayModes.DESIGN}
          >
            {!isNullOrUndefined(exp?.layers) && (
              <LayersRenderer
                canvasRefs={getCanvasRefs}
                drawingLayer={drawingLayer}
                errorLayer={errorLayer}
                errorLayerIconRef={errorLayerIconRef}
                selectionColor={selectedLayerColor}
                strokeNormalizer={strokeNormalizer}
                setLayerErrorIcon={setLayerErrorIcon}
              />
            )}
          </Group>
          <CanvasDragAndDrop
            dragOverlayRect={dragOverlayRect}
            layerOverlayRect={layerOverlayRect}
          />
        </RMWCanvas>
      </div>
      <FileImportView
        importType={expDashboardContext.fileImportActionType}
        docData={exp}
        setImportType={expDashboardContext.setFileImportActionType}
      />
    </>
  );
}

export default ExperimentCanvas;
